/* `x` is the current sensor's scaled raw value. References such as d1 and
 * d5 resolve other configured sensor values, including their own formulas. */
const IDENTIFIER = /\b[A-Za-z_][A-Za-z0-9_]*\b/g;

const FORMULA_FUNCTIONS = {
  abs: Math.abs,
  ceil: Math.ceil,
  floor: Math.floor,
  log: Math.log,
  log10: Math.log10,
  max: Math.max,
  min: Math.min,
  pow: Math.pow,
  sqrt: Math.sqrt,
  round: (number, places = 0) => {
    const factor = 10 ** Number(places);
    return Math.round(Number(number) * factor) / factor;
  },
  avg: (...values) => values.reduce((sum, current) => sum + Number(current), 0) / values.length,
  clamp: (value, low, high) => Math.min(Math.max(Number(value), Number(low)), Number(high)),
  ifnull: (value, fallback) => (value === null || value === undefined || value === "" ? fallback : value),
  pct: (part, total) => (Number(part) / Number(total)) * 100,
  scale: (value, inputLow, inputHigh, outputLow, outputHigh) => (
    ((Number(value) - Number(inputLow)) / (Number(inputHigh) - Number(inputLow)))
      * (Number(outputHigh) - Number(outputLow)) + Number(outputLow)
  ),
};

const normalizeFormula = (formula) => {
  const expression = String(formula || "").trim();
  if (!expression) return "";
  const normalized = expression
    .replace(/\bX\b/g, "x")
    .replace(/\bdata\.([A-Za-z_][A-Za-z0-9_]*)\b/gi, "$1");
  return /^[+*/-]/.test(normalized) ? `x${normalized}` : normalized;
};

const flattenTelemetry = (liveData = {}) => ({
  ...(liveData?.payload && typeof liveData.payload === "object" ? liveData.payload : {}),
  ...(liveData?.data && typeof liveData.data === "object" ? liveData.data : {}),
  ...liveData,
});

const readValue = (values, key) => {
  const match = Object.keys(values || {}).find((candidate) => String(candidate).toLowerCase() === String(key || "").toLowerCase());
  return match === undefined ? undefined : values[match];
};

const isIfNullFallbackValue = (expression, identifier) => {
  const escaped = String(identifier).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\bifnull\\s*\\(\\s*${escaped}\\s*,`, "i").test(expression);
};

const evaluateFormula = (formula, value, values = {}) => {
  const expression = normalizeFormula(formula);
  if (!expression) return Number(value);
  const withoutDecimalLiterals = expression.replace(/(?:\d+\.\d*|\.\d+)/g, "");
  if (!/^[\d\sA-Za-z_+*/%().,?:<>=!&|~-]+$/.test(expression) || /[[\]{};.]/.test(withoutDecimalLiterals)) {
    throw new Error("Formula contains unsupported characters");
  }

  const context = Object.entries(values).reduce((result, [key, raw]) => {
    const numeric = Number(raw);
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(key) && Number.isFinite(numeric)) result[key.toLowerCase()] = numeric;
    return result;
  }, { x: Number(value) });
  const names = [];
  const args = [];

  [...new Set(expression.match(IDENTIFIER) || [])].forEach((identifier) => {
    const key = identifier.toLowerCase();
    if (FORMULA_FUNCTIONS[key]) {
      names.push(identifier);
      args.push(FORMULA_FUNCTIONS[key]);
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(context, key)) {
      if (isIfNullFallbackValue(expression, identifier)) {
        names.push(identifier);
        args.push(null);
        return;
      }
      throw new Error(`Unknown formula value: ${identifier}`);
    }
    names.push(identifier);
    args.push(context[key]);
  });

  // eslint-disable-next-line no-new-func
  const result = new Function(...names, `"use strict"; return (${expression});`)(...args);
  if (!Number.isFinite(Number(result))) throw new Error("Formula result is not a number");
  return Number(result);
};

export const getSensorValues = (sensors = [], liveData = {}) => {
  const telemetry = flattenTelemetry(liveData);
  const sensorByKey = new Map(sensors.map((sensor) => [String(sensor.key || "").toLowerCase(), sensor]));
  const resolved = {};
  const resolving = new Set();

  const resolveSensor = (sensor) => {
    const key = String(sensor?.key || "").toLowerCase();
    if (!key || Object.prototype.hasOwnProperty.call(resolved, key)) return resolved[key] ?? null;
    if (resolving.has(key)) throw new Error(`Circular formula reference: ${sensor.key}`);

    const raw = readValue(telemetry, sensor.key);
    if (raw === undefined || raw === null || raw === "") {
      resolved[key] = null;
      return null;
    }
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
      resolved[key] = null;
      return null;
    }

    resolving.add(key);
    const context = { ...telemetry, ...resolved };
    const formula = normalizeFormula(sensor.formula);
    (formula.match(IDENTIFIER) || []).forEach((identifier) => {
      const dependency = sensorByKey.get(identifier.toLowerCase());
      if (dependency && dependency !== sensor) {
        const value = resolveSensor(dependency);
        if (value !== null) context[dependency.key] = value;
      }
    });

    const factored = numeric * Number(sensor.factor || 1);
    const value = formula ? evaluateFormula(formula, factored, context) : factored;
    resolving.delete(key);
    resolved[key] = Number(value.toFixed(6));
    return resolved[key];
  };

  sensors.forEach((sensor) => {
    try {
      resolveSensor(sensor);
    } catch (error) {
      const key = String(sensor?.key || "").toLowerCase();
      console.warn(`Formula error for sensor "${sensor?.key}" ("${sensor?.formula}"):`, error);
      resolving.delete(key);
      resolved[key] = null;
    }
  });
  return resolved;
};

export const getSensorValue = (sensor, liveData, sensors = []) => {
  if (!sensor || !liveData) return null;
  try {
    const values = getSensorValues(sensors.length ? sensors : [sensor], liveData);
    return values[String(sensor.key || "").toLowerCase()] ?? null;
  } catch (error) {
    console.warn(`Formula error for sensor "${sensor.key}" ("${sensor.formula}"):`, error);
    return null;
  }
};

export const formatSensorValue = (sensor, value) => {
  if (value === null || value === undefined) return "--";
  const numeric = Number(value);
  const display = Number.isFinite(numeric)
    ? numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : String(value);
  return sensor.unit ? `${display} ${sensor.unit}` : display;
};
